import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, ExternalLink, Clock, CreditCard } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Skeleton } from '../ui/Skeleton';
import { getCurrencySymbol, API_CONFIG } from '../../constants/index.js';
import axios from 'axios';

const DonationsList = ({ projectId, limit = 5 }) => {
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDonations = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Use new combined endpoint
        const response = await axios.get(
          `${API_CONFIG.BASE_URL}/api/projects/${projectId}/recent-donations`,
          { params: { limit } }
        );
        
        // API returns { success: true, data: { success: true, donations: [...] } }
        const donations = response.data?.data?.donations || response.data?.donations || [];
        setDonations(donations);
      } catch (err) {
        console.error('Failed to fetch donations:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (projectId) {
      fetchDonations();
    }
  }, [projectId, limit]);

  const formatTimeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} days ago`;
    
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary-teal/10 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-primary-teal" />
            </div>
            Recent Donations
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {[...Array(3)].map((_, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Skeleton className="w-10 h-10 rounded-full" />
                <div>
                  <Skeleton className="h-4 w-20 mb-1" />
                  <Skeleton className="h-3 w-16" />
                </div>
              </div>
              <Skeleton className="h-5 w-12" />
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary-teal/10 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-primary-teal" />
            </div>
            Recent Donations
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-neutral-500">
            <p className="text-sm">Unable to load donations</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (donations.length === 0) {
    return (
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary-teal/10 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-primary-teal" />
            </div>
            Recent Donations
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-neutral-500">
            <TrendingUp className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No donations yet</p>
            <p className="text-xs mt-1">Be the first to support this project!</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-sm hover:shadow-md transition-shadow duration-300">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2">
          <div className="w-8 h-8 bg-primary-teal/10 rounded-lg flex items-center justify-center">
            <TrendingUp className="w-4 h-4 text-primary-teal" />
          </div>
          Recent Donations
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {donations.map((donation, index) => {
          const isPi = donation.type === 'pi';
          const currencySymbol = getCurrencySymbol(donation.currency);
          
          return (
            <motion.div
              key={`${donation.type}-${donation.id}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="flex items-center justify-between p-3 bg-gradient-to-r from-neutral-50 to-neutral-100/50 rounded-lg hover:shadow-sm transition-all duration-200 hover:scale-[1.02]"
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center border ${
                  isPi 
                    ? 'bg-primary-teal/10 border-primary-teal/20' 
                    : 'bg-green-50 border-green-200'
                }`}>
                  {isPi ? (
                    <TrendingUp className="w-5 h-5 text-primary-teal" />
                  ) : (
                    <CreditCard className="w-5 h-5 text-green-600" />
                  )}
                </div>
                <div>
                  <p className="font-medium text-neutral-900 text-sm">
                    {donation.reference || 'Processing...'}
                  </p>
                  <div className="flex items-center gap-1 text-xs text-neutral-600">
                    <Clock className="w-3 h-3" />
                    {formatTimeAgo(donation.timestamp)}
                    {!isPi && donation.paymentChannel && (
                      <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                        {donation.paymentChannel}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <p className={`font-bold ${isPi ? 'text-primary-teal' : 'text-green-600'}`}>
                  {currencySymbol}{new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(donation.amount)}
                </p>
                {isPi && donation.explorerUrl && (
                  <a 
                    href={donation.explorerUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-neutral-500 hover:text-primary-teal transition-colors flex items-center gap-1 justify-end"
                  >
                    Verify
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </motion.div>
          );
        })}
        
        {donations.length >= limit && (
          <div className="text-center pt-2">
            <p className="text-xs text-neutral-500">
              Showing recent {limit} donations
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DonationsList;
